import { apiClient } from './api'
import {
  AcceptInvitationResponse,
  ClaimEmailDomainRequest,
  CreateOrganizationRequest,
  EmailDomainClaimResponse,
  EmailDomainClaimResult,
  InvitationResponse,
  InvitationsResponse,
  MembersResponse,
  MembershipResponse,
  EmailDomainVerification,
  EmailDomainVerificationFailure,
  EmailDomainVerificationResponse,
  Organization,
  OrganizationResponse,
  OrgInquirySummary,
  OrgInvitation,
  OrgMemberInquiries,
  OrgMembership,
  OrgPaginationParams,
  OrgRole,
  UserOrganization,
  UserOrganizationsResponse,
} from '@/types/organization'

/**
 * A domain-verification redemption that failed, carrying *why* as a discrete
 * `reason` derived from the HTTP status — never from the message text.
 */
export class EmailDomainVerificationError extends Error {
  readonly reason: EmailDomainVerificationFailure

  constructor(reason: EmailDomainVerificationFailure, message: string) {
    super(message)
    this.name = 'EmailDomainVerificationError'
    this.reason = reason
  }
}

class OrganizationService {
  private api = apiClient

  async createOrganization(payload: CreateOrganizationRequest): Promise<Organization> {
    const response = await this.api.post<OrganizationResponse>('/organizations', payload)
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create organization')
    }
    return response.data.data
  }

  async listOrganizations(): Promise<UserOrganization[]> {
    const response = await this.api.get<UserOrganizationsResponse>('/organizations')
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load organizations')
    }
    return response.data.data
  }

  async getOrganization(orgId: string): Promise<Organization> {
    const response = await this.api.get<OrganizationResponse>(`/organizations/${orgId}`)
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load organization')
    }
    return response.data.data
  }

  async updateOrganization(
    orgId: string,
    payload: Partial<CreateOrganizationRequest>,
  ): Promise<Organization> {
    const response = await this.api.patch<OrganizationResponse>(`/organizations/${orgId}`, payload)
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to update organization')
    }
    return response.data.data
  }

  async deleteOrganization(orgId: string): Promise<void> {
    const response = await this.api.delete(`/organizations/${orgId}`)
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete organization')
    }
  }

  /**
   * Claim an email domain for an organization. OWNER/ADMIN only.
   *
   * Re-claiming is how a challenge is re-sent — the endpoint is idempotent in
   * the sense that it simply overwrites the claim and mints a fresh token, so a
   * user who never received the email can just submit again.
   */
  async claimEmailDomain(
    orgId: string,
    payload: ClaimEmailDomainRequest,
  ): Promise<EmailDomainClaimResult> {
    const response = await this.api.post<EmailDomainClaimResponse>(
      `/organizations/${orgId}/domain`,
      payload,
    )
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to claim the email domain')
    }
    return response.data.data
  }

  async listMembers(orgId: string): Promise<OrgMembership[]> {
    const response = await this.api.get<MembersResponse>(`/organizations/${orgId}/members`)
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load members')
    }
    return response.data.data
  }

  async updateMemberRole(orgId: string, userId: string, role: OrgRole): Promise<OrgMembership> {
    const response = await this.api.patch<MembershipResponse>(
      `/organizations/${orgId}/members/${userId}`,
      { role },
    )
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to update member role')
    }
    return response.data.data
  }

  async removeMember(orgId: string, userId: string): Promise<void> {
    const response = await this.api.delete(`/organizations/${orgId}/members/${userId}`)
    if (!response.success) {
      throw new Error(response.error || 'Failed to remove member')
    }
  }

  async listInvitations(orgId: string): Promise<OrgInvitation[]> {
    const response = await this.api.get<InvitationsResponse>(`/organizations/${orgId}/invitations`)
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load invitations')
    }
    return response.data.data
  }

  async createInvitation(orgId: string, email: string, role: OrgRole): Promise<OrgInvitation> {
    const response = await this.api.post<InvitationResponse>(
      `/organizations/${orgId}/invitations`,
      { email, role },
    )
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to send invitation')
    }
    return response.data.data
  }

  async revokeInvitation(orgId: string, inviteId: string): Promise<void> {
    const response = await this.api.delete(`/organizations/${orgId}/invitations/${inviteId}`)
    if (!response.success) {
      throw new Error(response.error || 'Failed to revoke invitation')
    }
  }

  /**
   * Per-member bid/RFQ counts for the whole organization. OWNER/ADMIN only.
   *
   * This is what the members list uses to show activity — one call for every
   * member, rather than an N+1 fan-out of `getMemberInquiries`.
   *
   * Unlike the other org endpoints the body is NOT `{ data: <payload> }`; the
   * paginated response *is* the body, so we return `response.data` directly.
   */
  async getInquirySummary(
    orgId: string,
    params?: OrgPaginationParams,
  ): Promise<OrgInquirySummary> {
    const response = await this.api.get<OrgInquirySummary>(
      `/organizations/${orgId}/inquiries/summary`,
      { params },
    )
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load organization activity')
    }
    return response.data
  }

  /**
   * One member's bids and RFQs, as two independently paginated blocks.
   * OWNER/ADMIN only; a user outside the caller's org is a 404.
   *
   * `limit` is clamped server-side to [1, 50].
   */
  async getMemberInquiries(
    orgId: string,
    userId: string,
    params?: OrgPaginationParams,
  ): Promise<OrgMemberInquiries> {
    const response = await this.api.get<OrgMemberInquiries>(
      `/organizations/${orgId}/members/${userId}/inquiries`,
      { params },
    )
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to load member inquiries')
    }
    return response.data
  }

  async acceptInvitation(token: string): Promise<AcceptInvitationResponse['data']> {
    const response = await this.api.post<AcceptInvitationResponse>(
      `/organizations/invitations/${token}/accept`,
    )
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to accept invitation')
    }
    return response.data.data
  }

  /**
   * Redeem the token from an organization domain-verification email.
   *
   * UNAUTHENTICATED by design — the challenge is mailed to an address at the
   * claimed domain, typically an IT/ops contact with no SecondLane account, so
   * the backend excludes this path from the auth middleware. Any `Authorization`
   * header the client happens to have is irrelevant to the outcome.
   *
   * The token is single-use. Failures are thrown as `EmailDomainVerificationError`
   * with a `reason` taken from the HTTP status:
   *   400 → `expired`  (a new email can be requested)
   *   404 → `invalid`  (unknown OR already redeemed — deliberately the same answer)
   *   anything else → `error`
   */
  async verifyEmailDomain(token: string): Promise<EmailDomainVerification> {
    const response = await this.api.post<EmailDomainVerificationResponse>(
      `/organizations/domain/verify/${encodeURIComponent(token)}`,
    )
    if (!response.success || !response.data) {
      const reason: EmailDomainVerificationFailure =
        response.status === 400 ? 'expired' : response.status === 404 ? 'invalid' : 'error'
      throw new EmailDomainVerificationError(
        reason,
        response.error || 'Failed to verify the email domain',
      )
    }
    return response.data.data
  }
}

export const organizationService = new OrganizationService()
